// Under the Gate (Oct 27, programming-plan.md Phase E; its own screen): ships due under the Golden Gate in the next two
// hours, from the sidecar's listen to aisstream.io (`ships` in the snapshot). In or out, what it is and how long, its
// name and where it's bound. Small boats (tugs, sailboats, pleasure craft, fishing) are left out unless they're long.
// A big ship due also opens the next long Views block on the Golden Gate approach (views-around-town.mjs) and gets a
// crawl line (ticker.mjs), through bigShipDue().
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import SnapshotDisplay from './snapshot-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import { time, asOf } from './format.mjs';

const MIN_LENGTH = 60;
const WINDOW_MIN = 120;
const STALE_MS = 20 * 60000;
const BIG_M = 200;

// AIS ship types, in words
const kindOf = (type) => {
	if (type >= 60 && type <= 69) return 'Passenger ship';
	if (type >= 70 && type <= 79) return 'Cargo ship';
	if (type >= 80 && type <= 89) return 'Tanker';
	return {
		30: 'Fishing boat', 31: 'Tow', 32: 'Tow', 35: 'Navy ship', 50: 'Pilot boat', 51: 'Rescue boat', 52: 'Tug', 55: 'Patrol boat',
	}[type] ?? 'Ship';
};
const worthIt = (s) => (s.length ?? 0) >= MIN_LENGTH || (s.type >= 60 && s.type <= 89);

// the ships still to come, counted from when the sidecar heard them
const comingNow = (now = new Date()) => {
	const ships = data('ships');
	if (!ships?.at || now - new Date(ships.at) > STALE_MS) return [];
	return (ships.coming ?? []).filter(worthIt).map((s) => ({ ...s, minutes: Math.round((new Date(s.eta) - now) / 60000) }))
		.filter((s) => s.minutes >= 0 && s.minutes <= WINDOW_MIN);
};

// a ship of BIG_M or more due under the bridge between `from` and `to` minutes from now
const bigShipDue = (from, to, now = new Date()) => comingNow(now).find((s) => (s.length ?? 0) >= BIG_M && s.minutes >= from && s.minutes <= to) ?? null;
const describe = (s) => `${kindOf(s.type)}${s.length ? `, ${s.length} m` : ''}`;

class UnderTheGate extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Under the Gate', { seconds: 14 });
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return comingNow().length > 0;
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		const rows = comingNow(now).slice(0, 5).map((s) => `<div class="row"><div class="t">${time(s.eta).replace(/ [AP]M$/, '')}</div><div class="io">${s.inbound ? 'IN' : 'OUT'}</div>
			<div>${escapeHtml(describe(s))}<span class="dim">${s.name ? ` &middot; ${escapeHtml(s.name)}` : ''}${s.destination ? ` &middot; ${escapeHtml(s.destination)}` : ''}</span></div></div>`);
		const biggest = comingNow(now).reduce((best, s) => ((s.length ?? 0) > (best?.length ?? 0) ? s : best), null);
		return `<div class="hdr">Next 2 hours at the Golden Gate</div>
			<div class="list ships">${rows.join('')}</div>
			${biggest?.length >= BIG_M ? `<div class="small gap">Biggest: ${biggest.length} m, ${biggest.inbound ? 'inbound' : 'outbound'} about ${time(biggest.eta)}</div>` : ''}
			<div class="asof">AIS: aisstream.io &middot; ${asOf(fetchedAt('ships'), now)}</div>`;
	}
}

registerDisplay(new UnderTheGate(36, 'under-the-gate'));

export { bigShipDue, describe };
